import { AxiosRequestConfig } from 'axios';
import { RolesEnum } from 'core/models';
import store from 'store';
import { getStoredAccessToken } from 'utils/localStorage';
import { timeZone } from 'utils/util';

const devUrl = process.env.REACT_APP_API_DEV_URL || '/api/backend';
const prodUrl = process.env.REACT_APP_API_PROD_URL || '/api/backend';

export const apiBaseUrl =
  process.env.NODE_ENV === 'development' ? devUrl : prodUrl;

// eslint-disable-next-line no-shadow
export enum AuthActionsEnum {
  AUTH_ACTION_VERIFY = 'verifySignupSetPasswordLong',
  AUTH_ACTION_SEND_RESTORE = 'sendResetPwd',
  AUTH_ACTION_RESTORE = 'resetPwd',
}

export const LOGOUT_TIMEOUT = 15 * 60 * 1000;

export interface Config {
  paths: {
    baseUrl: string;
    [route: string]: string;
  };
}

const tenantPaths = {
  tenant: '/management/tenant',
  tenantDevice: '/management/tenant-device',
};

export const sortMap: any = {
  generateTime: 'created_at',
};

export const config: Config = {
  paths: {
    api: apiBaseUrl,
    baseUrl: '/',
    signIn: '/sign-in',
    download: '/download',
    downloads: '/appDownloads',
    forbidden: '/403',
    notFound: '/404',
    addPath: '/add',
    editPath: '/edit',
    profile: '/profile',
    apikey: '/apikey',
    agentSettings: '/agent-settings',
    // The OAuth consent screen. Inside the ordinary bundle so an unauthenticated
    // visitor routes through the normal sign-in, SSO and MFA flow and returns here.
    oauthConsent: '/oauth/consent',
    mcpSetting: '/settings/mcp',
    authentication: '/authentication',
    totpConfirm: '/totpConfirm',

    dataAnalytics: '/dataAnalytics',
    assignedUsers: '/assignedUsers',
    dataAnalyticsDetails: '/details',

    dashboard: '/monitoring',
    rpmRT: '/monitoring/lives',
    rpmRTList: '/monitoring/lives/list',
    addRpmPatient: '/monitoring/lives/patient/add',
    dashboardEvent: '/monitoring/events',
    dashboardRpmAlert: '/monitoring/alerts',
    monitoringProtocols: '/monitoring/monitoringProtocol',
    patientsPath: '/monitoring/patientManagement',
    patients: '/monitoring/patientManagement',
    process: '/monitoring/process',

    management: '/management',
    userManagement: '/management/users',
    sensors: '/management/sensors',
    devices: '/management/deivces',
    sites: '/management/sites',

    pmManagement: '/pm-management',
    adminManagement: '/admin-management',
    techManagement: '/tech-management',
    physicianManagement: '/physician-management',
    researchersManagement: '/researchers-management',
    clinicalTrialCoordinatorManagement: '/ctc-management',

    clinics: '/management/tenant',
    changePassword: '/change-password',
    restorePassword: '/restore-password',
    rechangePassword: '/rechange-Password',
    verifyPassword: '/verify-password',
    patientDashboard: '/patient-dashboard',
    disclosureView: '/disclosure-view',
    ecgDataChunks: '/ecg-data-chunks',
    pec: '/pec',
    reports: '/reports',
    reportArchive: '/reportArchive',
    generateReport: '/generateReport',
    reportsPdf: '/reportsPdf',
    reportsAbpmPdf: '/reportsAbpmPdf',
    settings: '/settings',
    analysisSetting: '/settings/analysis-setting',
    emailTemplate: '/settings/emailTemplate',
    smsTemplate: '/settings/smsTemplate',
    ssoSetting: '/settings/sso',
    patientTemplate: '/settings/patientTemplate',
    otherSetting: '/settings/other-setting',
    groupManagement: '/settings/groupmanagement',
    historyView: '/history',
    ecgRecord: '/ecg-record',
    studies: '/studies',
    studiesManagement: '/studies-management',
    superAdminManagement: 'super-admin-management',
    st: 'studies-sites',

    orderManagement: '/order',

    supersetContainer: '/superset-container',
    supersetCalendar: '/superset-calendar',

    ...tenantPaths,
  },
};

const webVersionInfoVersion = process.env.REACT_APP_VERSION_INFO_VERSION;

export const axiosDefaultConfig = (): AxiosRequestConfig => {
  let version =
    store.getState().session.user?.webVersionInfo?.version ??
    webVersionInfoVersion;
  if (version == null) {
    version = '';
  }

  return getStoredAccessToken()
    ? {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getStoredAccessToken()}`,
          timeZone: timeZone(),
          version,
        },
      }
    : {
        headers: {
          'Content-Type': 'application/json',
          timeZone: timeZone(),
          version,
        },
      };
};

export interface ParamId {
  id: string;
  tenantName: string;
}

export const ruleTrans: Record<string, string> = {
  // 整个系统的超管
  vivalink_super: RolesEnum.VivaLNKAdmin,
  // tenant级别的超管
  tenant_admin: RolesEnum.TenantAdmin,
  super_admin: RolesEnum.SuperAdmin,
  admin: RolesEnum.Admin,
  program_manager: RolesEnum.ProgramManager,
  // 技术人员
  technician: RolesEnum.Technician,
  // 医生
  physician: RolesEnum.Physician,
  // 研究人员
  researcher: RolesEnum.Researcher,
  // 临床试验协调员
  clinical_trial_coordinator: RolesEnum.ClinicalTrialCoordinator,
  // 分析员
  analyst: RolesEnum.Analyst,
  enrollment_coordinator: RolesEnum.EnrollmentCoordinator,
};

interface CustomClinicParam {
  insurance: string[];
  predicate: string[];
}

export const customClinicConfig: CustomClinicParam = {
  predicate: ['Predicate', 'Predicate-Test', 'testUAT', 'Predicate-Reallife'],
  insurance: [
    'Eric4-0',
    'vl123',
    'vl123demo',
    'Vivalink-202301',
    'VL-RPM',
    'ClinicA',
  ],
};
